import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Admin grade override: stored globally, sent as header on all API requests
let adminGradeOverride: number | null = null;
export function setAdminGradeOverride(grade: number | null) { adminGradeOverride = grade; }
export function getAdminGradeOverride(): number | null { return adminGradeOverride; }

export function getExtraHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (adminGradeOverride) headers['x-admin-grade-override'] = String(adminGradeOverride);
  return headers;
}

class ApiError extends Error {
  public code?: string;
  public details?: string;
  public status: number;
  public response?: any;

  constructor(message: string, status: number, code?: string, details?: string, response?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.response = response;

    // Ensure properties are enumerable and accessible
    Object.defineProperty(this, 'code', { value: code, enumerable: true, writable: true });
    Object.defineProperty(this, 'details', { value: details, enumerable: true, writable: true });
    Object.defineProperty(this, 'status', { value: status, enumerable: true, writable: true });
    Object.defineProperty(this, 'response', { value: response, enumerable: true, writable: true });
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    
    // Try to parse as JSON to preserve structured error information
    try {
      const errorData = JSON.parse(text);
      const message = errorData.error || errorData.message || `${res.status}: ${text}`;
      
      throw new ApiError(
        message,
        res.status,
        errorData.code,
        errorData.details,
        errorData
      );
    } catch (parseError) {
      // Fallback to original behavior if not JSON
      throw new ApiError(`${res.status}: ${text}`, res.status);
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: { ...(data ? { "Content-Type": "application/json" } : {}), ...getExtraHeaders() },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: getExtraHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
